import React from "react";
import { Check, ChevronUp, ChevronDown, AlertTriangle } from "lucide-react";
import Sparkline from "./Sparkline";
import { fmtMoneyFull, fmtM, fmtPct, fmtNum } from "../lib/format";

const Pct = ({ v }) => {
  if (v === null || v === undefined) return <span className="text-[#94A7AE]">&mdash;</span>;
  const c = v >= 0 ? "#059669" : "#E11D48";
  return <span className="mono" style={{ color: c }}>{fmtPct(v)}</span>;
};

const Mg = ({ v }) => (v === null || v === undefined
  ? <span className="text-[#94A7AE]">&mdash;</span>
  : <span className="mono text-[#47616B]">{fmtNum(v)}%</span>);

const dash = <span className="text-[#94A7AE]">&mdash;</span>;
const na = <span className="text-[#94A7AE] text-xs">N/A</span>;

// Toggleable metric columns. `defaultOn: false` => hidden until the user enables it.
export const STOCK_COLUMNS = [
  { key: "score", label: "Score", defaultOn: true, render: (s) => <span className="mono text-[#0284C7] font-semibold">{fmtNum(s.score)}</span> },
  { key: "marketCap", label: "Mkt Cap", defaultOn: true, render: (s) => <span className="mono text-[#0B2830]">{fmtMoneyFull(s.marketCap)}</span> },
  { key: "peRatio", label: "P/E", defaultOn: true, render: (s) => <span className="mono text-[#0B2830]">{fmtNum(s.peRatio)}</span> },
  { key: "sector_median_pe", label: "Peer P/E", defaultOn: false, render: (s) => s.sector_median_pe != null ? <span className="mono text-[#7C949C]">{fmtNum(s.sector_median_pe)}</span> : dash },
  { key: "pe_vs_peer", label: "P/E \u00F7 Peer", defaultOn: true, render: (s) => s.pe_vs_peer != null ? <span className="mono" style={{ color: s.pe_vs_peer <= 1 ? "#059669" : "#47616B" }}>{fmtNum(s.pe_vs_peer, 2)}</span> : dash },
  { key: "forwardPE", label: "Fwd P/E", defaultOn: true, render: (s) => s.forwardPE != null ? <span className="mono text-[#0B2830]">{fmtNum(s.forwardPE)}</span> : dash },
  { key: "sector_median_fwd_pe", label: "Peer Fwd P/E", defaultOn: false, render: (s) => s.sector_median_fwd_pe != null ? <span className="mono text-[#7C949C]">{fmtNum(s.sector_median_fwd_pe)}</span> : dash },
  { key: "fwd_pe_vs_peer", label: "Fwd P/E \u00F7 Peer", defaultOn: true, render: (s) => s.fwd_pe_vs_peer != null ? <span className="mono" style={{ color: s.fwd_pe_vs_peer <= 1 ? "#059669" : "#47616B" }}>{fmtNum(s.fwd_pe_vs_peer, 2)}</span> : dash },
  { key: "forward_peg", label: "PEG (Fwd)", defaultOn: true, render: (s) => s.forward_peg != null ? <span className="mono text-[#0B2830]">{fmtNum(s.forward_peg, 2)}</span> : dash },
  { key: "forward_peg_2y", label: "PEG 2Y", defaultOn: false, render: (s) => s.forward_peg_2y != null ? <span className="mono text-[#0B2830]">{fmtNum(s.forward_peg_2y, 2)}</span> : na },
  { key: "forward_peg_3y", label: "PEG 3Y", defaultOn: false, render: (s) => s.forward_peg_3y != null ? <span className="mono text-[#0B2830]">{fmtNum(s.forward_peg_3y, 2)}</span> : na },
  { key: "forward_peg_5y", label: "PEG 5Y", defaultOn: false, render: (s) => s.forward_peg_5y != null ? <span className="mono text-[#0B2830]">{fmtNum(s.forward_peg_5y, 2)}</span> : na },
  { key: "revenue_growth", label: "Rev Growth", defaultOn: false, render: (s) => <Pct v={s.revenue_growth} /> },
  { key: "operating_margin", label: "Op Margin", defaultOn: false, render: (s) => <Mg v={s.operating_margin} /> },
  { key: "net_margin", label: "Net Margin", defaultOn: false, render: (s) => <Mg v={s.net_margin} /> },
  { key: "ebitda_margin", label: "EBITDA Margin", defaultOn: false, render: (s) => <Mg v={s.ebitda_margin} /> },
  { key: "ebitda_ttm_m", label: "EBITDA TTM", defaultOn: false, render: (s) => <span className="mono text-[#47616B]">{fmtM(s.ebitda_ttm_m)}</span> },
  { key: "ebitda_yoy", label: "EBITDA YoY", defaultOn: true, render: (s) => <Pct v={s.ebitda_yoy} /> },
  { key: "ebitda_growth_18m", label: "EBITDA Growth", defaultOn: true, render: (s) => <Pct v={s.ebitda_growth_18m} /> },
  { key: "payout_ratio", label: "Payout", defaultOn: true, render: (s) => s.payout_ratio != null ? <span className="mono text-[#47616B]">{fmtNum(s.payout_ratio)}%</span> : dash },
  { key: "buyback_12m_m", label: "Buyback 12M", defaultOn: true, render: (s) => <span className="mono text-[#0D9488] font-medium">{fmtM(s.buyback_12m_m)}</span> },
  { key: "buyback_3m_m", label: "Buyback 3M", defaultOn: false, render: (s) => <span className="mono text-[#47616B]">{fmtM(s.buyback_3m_m)}</span> },
  { key: "buyback_yield", label: "BB Yield %", defaultOn: false, render: (s) => s.buyback_yield != null ? <span className="mono text-[#D97706]">{fmtNum(s.buyback_yield, 2)}%</span> : dash },
];

export const DEFAULT_VISIBLE_COLUMNS = STOCK_COLUMNS.filter((c) => c.defaultOn).map((c) => c.key);

const th = "bg-[#E9F1F4] border-b border-[#D2E0E6] text-[10px] uppercase tracking-[0.12em] text-[#47616B] py-3 px-3 whitespace-nowrap select-none sticky top-0 z-20";
const td = "py-2.5 px-3 whitespace-nowrap text-sm border-b border-[#E6EEF1]";

export default function StockTable({ stocks, sortKey, sortDir, onSort, onRowClick, visibleColumns }) {
  // An explicit empty array means "hide all" (only fixed columns show).
  // Fall back to defaults only when the prop is not provided at all.
  const visible = Array.isArray(visibleColumns)
    ? STOCK_COLUMNS.filter((c) => visibleColumns.includes(c.key))
    : STOCK_COLUMNS.filter((c) => c.defaultOn);

  const arrow = (k) =>
    sortKey === k ? (sortDir === "desc" ? <ChevronDown className="inline w-3 h-3" /> : <ChevronUp className="inline w-3 h-3" />) : null;

  return (
    <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-sm overflow-auto max-h-[72vh] fade-up" data-testid="stock-table-scroll">
      <table className="w-full border-collapse" data-testid="stock-table">
        <thead>
          <tr>
            <th className={th + " text-center sticky left-0 top-0 z-30 bg-[#E9F1F4]"}>#</th>
            <th className={th + " text-left sticky left-8 top-0 z-30 bg-[#E9F1F4]"}>Company</th>
            <th className={th + " text-left"}>Sector</th>
            {visible.map((c) => (
              <th key={c.key} onClick={() => onSort(c.key)}
                className={th + " text-right cursor-pointer hover:text-[#0284C7]"} data-testid={`sort-${c.key}`}>
                {c.label} {arrow(c.key)}
              </th>
            ))}
            <th className={th + " text-center"}>18M Trend</th>
            <th className={th + " text-center"}>Peers</th>
          </tr>
        </thead>
        <tbody>
          {stocks.map((s, i) => (
            <tr key={s.ticker} data-testid={`row-${s.ticker}`} onClick={() => onRowClick && onRowClick(s)}
              className={`cursor-pointer hover:bg-[#E9F1F4] transition-colors ${s.qualifies ? "bg-[#0284C7]/[0.06]" : ""}`}>
              <td className={td + " text-center text-[#94A7AE] mono sticky left-0 bg-inherit"}>{i + 1}</td>
              <td className={td + " sticky left-8 bg-white"}>
                <div className="mono font-semibold text-[#0B2830] flex items-center gap-1.5">
                  {s.ticker}
                  {s.qualifies && (
                    <span title="Meets every criterion" className="inline-flex items-center justify-center w-4 h-4 bg-[#059669]/15 border border-[#059669]/30 rounded">
                      <Check className="w-2.5 h-2.5 text-[#059669]" />
                    </span>
                  )}
                  {s.risk_flag && (
                    <span data-testid={`risk-flag-${s.ticker}`} title={s.risk_flag.reason}
                      className="inline-flex items-center justify-center w-4 h-4 bg-[#E11D48]/12 border border-[#E11D48]/35 rounded">
                      <AlertTriangle className="w-2.5 h-2.5 text-[#E11D48]" />
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#7C949C] max-w-[180px] truncate">{s.name}</div>
              </td>
              <td className={td + " text-[#47616B] text-xs"}>
                {s.sector}
                {s.country && <div className="text-[#94A7AE]">{s.country}</div>}
              </td>
              {visible.map((c) => (
                <td key={c.key} className={td + " text-right"}>{c.render(s)}</td>
              ))}
              <td className={td + " text-center"}><Sparkline series={s.ebitda_series} testid={`spark-${s.ticker}`} /></td>
              <td className={td + " text-center"}>
                {s.beats_peers ? (
                  <span className="bg-[#0D9488]/10 text-[#0D9488] px-2 py-0.5 text-[10px] mono border border-[#0D9488]/25 rounded">BEATS</span>
                ) : (
                  <span className="bg-[#E9F1F4] text-[#94A7AE] px-2 py-0.5 text-[10px] mono border border-[#D2E0E6] rounded">&mdash;</span>
                )}
              </td>
            </tr>
          ))}
          {stocks.length === 0 && (
            <tr><td colSpan={visible.length + 5} className="text-center text-[#7C949C] py-12 text-sm">No stocks match the current filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
